import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useCage, useCageTimezone } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { useGetCageOverview } from '@/domain/cages';
import type { DomainCageOverview } from '@/domain/cages/overview.types';
import { useIsSmolt } from '@/hooks/useCageTypeFlags';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useHydrateOverviewDisplayDates } from '@/routes/Client/Cage/overviewCharts/useOverviewDateRange';
import { resolveWeightDaypartTag } from '@/routes/Client/Cage/utils/filter-graph-dayparts';

export interface DateRange {
    dateFrom?: string;
    dateTo?: string;
}

interface CageOverviewContextValue {
    /** Loading state of the overview query */
    loading: boolean;
    /** Error from the overview query, if any */
    error?: Error | null;
    /** Whether the overview query is fetching, a retry included. */
    isFetching: boolean;
    /** Fetches the overview again, for an error state's Try again. */
    refetch: () => void;
    /** Current daypart tag filter */
    daypartTag?: string;
    /**
     * Overview data, in the camelCase domain shape. Never null — an absent
     * response maps to an empty overview, so consumers read `data.days` and
     * `data.period.weight` without guarding. Use `loading` and `error` for
     * request state.
     */
    data: DomainCageOverview;
    /** Start date of the current date range (for display) */
    dateFrom?: string;
    /** End date of the current date range (for display) */
    dateTo?: string;
    /** First measurement date of this cage's biomass (passthrough from CageContext) */
    firstDateMeasurement?: string;
    /** Handler to update the date range and trigger a new query */
    onDateChange: (dates: DateRange) => void;
}

const CageOverviewContext = createContext<CageOverviewContextValue | undefined>(undefined);

CageOverviewContext.displayName = 'CageOverviewContext';

export function useCageOverview(): CageOverviewContextValue {
    const context = useContext(CageOverviewContext);

    if (context == null) {
        throw new Error('useCageOverview must be used within a CageOverviewProvider');
    }

    return context;
}

const QUERY_DATE_FROM = 'date-from';
const QUERY_DATE_TO = 'date-to';

interface UseCageOverviewDateRangeResult {
    /** Date used to drive the overview query (resolved from URL only) */
    queryDateFrom?: string;
    queryDateTo?: string;
    /** Date shown to consumers (URL value, or derived from backend lastDay) */
    dateFrom?: string;
    dateTo?: string;
    /** Update URL params and trigger a re-query */
    onDateChange: (dates: DateRange) => void;
    /**
     * Set the display dates shown while the URL carries no range, without touching the
     * URL. They belong to the cage they were set for.
     */
    setDisplayDates: (from: string | undefined, to: string | undefined) => void;
}

/** Display dates hydrated for one cage. */
interface HydratedDisplayDates {
    cageId: number | undefined;
    from: string | undefined;
    to: string | undefined;
}

/**
 * Reusable hook for the cage overview date-range state.
 *
 * Splits the date-range responsibilities into three clear concerns:
 * 1. Query dates — read from URL params `date-from` / `date-to`.
 * 2. Display dates — what consumers render. Defaults to URL when set; otherwise
 *    callers can hydrate them from a backend value (e.g. `lastDay`) via
 *    `setDisplayDates` without forcing a re-query.
 * 3. `onDateChange` — updates URL params, which propagates to query + display.
 *
 * Keeps those apart instead of interleaving `useState + useEffect + useMemo`,
 * which is what the earlier provider did.
 */
export function useCageOverviewDateRange(cageId?: number): UseCageOverviewDateRangeResult {
    const [queryParams, setQueryParams] = useQueryParams();

    const [hydrated, setHydrated] = useState<HydratedDisplayDates | null>(null);

    const getQueryDate = useCallback(
        (key: string) => {
            const value = queryParams[key];
            return typeof value === 'string' && value.trim() !== '' ? value : undefined;
        },
        [queryParams]
    );

    const queryDateFrom = useMemo(() => getQueryDate(QUERY_DATE_FROM), [getQueryDate]);
    const queryDateTo = useMemo(() => getQueryDate(QUERY_DATE_TO), [getQueryDate]);

    // Derived, with hydrated dates tagged by cage, so a pen switch never shows the last
    // pen's dates. Clearing them in an effect instead left them empty for good when a
    // cached pen with the same lastDay gave the hydrate effect nothing new to react to.
    const hasUrlRange = queryDateFrom != null && queryDateTo != null;
    const hydratedForCage = hydrated?.cageId === cageId ? hydrated : null;
    const dateFrom = hasUrlRange ? queryDateFrom : hydratedForCage?.from;
    const dateTo = hasUrlRange ? queryDateTo : hydratedForCage?.to;

    // A new setter per cage reruns the caller's hydrate effect after a pen switch.
    const setDisplayDates = useCallback(
        (from: string | undefined, to: string | undefined) => {
            setHydrated({ cageId, from, to });
        },
        [cageId]
    );

    const onDateChange = useCallback(
        (dates: DateRange) => {
            const newDateFrom = dates.dateFrom;
            const newDateTo = dates.dateTo;

            const datesChanged = newDateFrom !== queryDateFrom || newDateTo !== queryDateTo;

            if (!datesChanged) {
                return;
            }

            setQueryParams({
                [QUERY_DATE_FROM]: newDateFrom ?? null,
                [QUERY_DATE_TO]: newDateTo ?? null,
            });
        },
        [queryDateFrom, queryDateTo, setQueryParams]
    );

    return {
        queryDateFrom,
        queryDateTo,
        dateFrom,
        dateTo,
        onDateChange,
        setDisplayDates,
    };
}

interface CageOverviewProviderProps {
    children: ReactNode;
}

export function CageOverviewProvider(props: CageOverviewProviderProps): ReactNode {
    const { children } = props;
    const { daypartTag: filterDaypartTag, selectedFishGroupId } = useCageFilter();
    const { cageId, dataAvailability } = useCage();
    // A smolt pen's cards and charts read `all`, as its smolt report does.
    const daypartTag = resolveWeightDaypartTag(useIsSmolt(), filterDaypartTag);
    const firstDateMeasurement = dataAvailability?.firstMeasurementDay;

    const { queryDateFrom, queryDateTo, dateFrom, dateTo, onDateChange, setDisplayDates } =
        useCageOverviewDateRange(cageId);

    const overviewParams = useMemo(
        () => ({
            from: queryDateFrom,
            to: queryDateTo,
            daypartTag,
            // The cohort named in the title and breadcrumb (`fg`), so the charts
            // answer for the same group the page says it is about.
            fishGroupId: selectedFishGroupId,
        }),
        [queryDateFrom, queryDateTo, daypartTag, selectedFishGroupId]
    );

    const { data, isLoading, error, isFetching, refetch } = useGetCageOverview(
        cageId,
        overviewParams
    );

    useHydrateOverviewDisplayDates({
        lastDay: data.biomass.estimate.lastDay || undefined,
        isLoading,
        queryDateFrom,
        queryDateTo,
        timezone: useCageTimezone(),
        setDisplayDates,
    });

    const contextValue = useMemo<CageOverviewContextValue>(
        () => ({
            loading: isLoading,
            error,
            isFetching: isFetching ?? false,
            refetch,
            data,
            daypartTag,
            firstDateMeasurement,
            dateFrom,
            dateTo,
            onDateChange,
        }),
        [
            isLoading,
            error,
            isFetching,
            refetch,
            data,
            daypartTag,
            firstDateMeasurement,
            dateFrom,
            dateTo,
            onDateChange,
        ]
    );

    return <CageOverviewContext value={contextValue}>{children}</CageOverviewContext>;
}
